// =============================================================================
// src/features/explore/explore-vote-data.ts — Derivacao pura dos modos de voto.
//
// O dataset so tem partido vencedor (p), % do vencedor (pct), 2º (s) e eleitoresK.
//   `regionVoteDistribution` SINTETIZA, de forma DETERMINISTA, uma distribuicao de
//   votos por candidato numa regiao que SOMA 100% e tem a forma do Datapedia (1º e
//   2º proximos, depois queda). A MESMA distribuicao alimenta a bolha (linha do
//   candidato) e o popup de ranking (tabela completa) — uma fonte unica, coerente.
//
// SRP: so matematica pura (sem DOM/Leaflet). O registro de candidatos vem do roster
//   (sem ciclo). Cor da bolha/calor = cor do PARTIDO (PARTY_COLORS), nao o teal.
// =============================================================================

import type { ElectoralYear, MunicipalityId, ZonaKey, HexColor, ZoneElectoralResult } from '../../app/types';
import { EXPLORE_DATASET, MANAUS_ELECTORAL_BY_YEAR, MANAUS_ZONA_POP_DATA } from '../../data';
import { PARTY_COLORS } from './constants';
import {
  BUBBLE_BASE_RADIUS,
  BUBBLE_MAX_RADIUS,
  HEAT_MIN_RADIUS,
  HEAT_MAX_RADIUS,
  HEAT_MIN_OPACITY,
  HEAT_MAX_OPACITY,
  CLAY_WASH,
  INK,
} from './explore-vote-constants';
import {
  buildSyntheticCandidates,
  candidateIdOf,
  type SyntheticCandidate,
  type VoteCandidateSel,
} from './explore-vote-roster';

export type { VoteCandidateSel } from './explore-vote-roster';

/** Linha derivada por regiao para o overlay (a "fatia" do candidato escolhido). */
export interface VoteRow {
  readonly votosAbsolutos: number;
  readonly percentual: number;
  /** Colocacao do candidato entre TODOS na regiao (1..N). */
  readonly ranking: number;
  /** Partido do candidato (ou vencedor da regiao no modo agregado). */
  readonly party: string;
}

/** Entrada do ranking de uma regiao (um candidato): votos + % + colocacao. */
export interface RegionVoteEntry {
  readonly cand: SyntheticCandidate;
  readonly votos: number;
  readonly pct: number;
  readonly posicao: number;
}

/** Insumos de uma regiao p/ a distribuicao (município ou zona). */
export interface RegionVoteInput {
  readonly winnerParty: string;
  readonly winnerPct: number;
  readonly secondParty: string;
  readonly totalValidos: number;
}

/** Cor neutra de fallback (paridade com explore-styling). */
const FALLBACK_COLOR: HexColor = '#B7B3AA';
/** Comparecimento fixo das zonas de Manaus (zonas nao tem IDH no dataset). */
const ZONE_TURNOUT = 0.82;
/** Razao do decaimento geometrico da "cauda" do ranking (3º em diante). */
const TAIL_DECAY = 0.6;
/** Fracao do voto restante que o 2º colocado puxa (mantem 1º e 2º proximos). */
const SECOND_SHARE = 0.86;

/** Clamp a [0,1]. */
function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value;
}

/** Arredonda a 1 casa decimal. */
function round1(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * Comparecimento estimado (0..1) — funcao DETERMINISTA do IDH (proxy de
 * urbanizacao/engajamento). Faixa saneada 0.65..0.88. NAO e dado real.
 */
export function turnoutDe(idh: number): number {
  return Math.min(Math.max(0.70 + idh * 0.18, 0.65), 0.88);
}

/** Votos validos estimados de um municipio (eleitoresK em milhares × comparecimento). */
export function votosValidosDe(munId: MunicipalityId): number {
  const mun = EXPLORE_DATASET.munis[munId];
  if (!mun) return 0;
  return mun.eleitoresK * 1000 * turnoutDe(mun.idh);
}

/** Partido vencedor da cidade no ano (ou null sem dado). */
export function winnerPartyOf(munId: MunicipalityId, year: ElectoralYear): string | null {
  return EXPLORE_DATASET.munis[munId]?.el[year]?.p ?? null;
}

/** Cor da bolha = cor do partido (vocabulario de dado), com fallback neutro. */
export function bubbleColorOf(party: string): HexColor {
  return PARTY_COLORS[party] ?? FALLBACK_COLOR;
}

// ── distribuicao coerente por regiao ─────────────────────────────────────────

/** % do 2º colocado: fatia do restante, sempre ABAIXO do 1º (e ≤ 100−w → cauda ≥ 0). */
function secondPctOf(winnerPct: number): number {
  return round1(Math.min((100 - winnerPct) * SECOND_SHARE, winnerPct - 1));
}

/** Distribui a cauda (3º+) por decaimento geometrico, somando exatamente `tail`. */
function assignTail(tailCandidates: readonly SyntheticCandidate[], tail: number, pcts: Map<string, number>): void {
  let weightSum = 0;
  for (let k = 0; k < tailCandidates.length; k += 1) weightSum += Math.pow(TAIL_DECAY, k);
  tailCandidates.forEach((cand, k) => pcts.set(cand.id, tail * Math.pow(TAIL_DECAY, k) / weightSum));
}

/** % de cada candidato por id: 1º=w, 2º=secondPct, demais=cauda geometrica (soma 100). */
function distributionPercents(input: RegionVoteInput): Map<string, number> {
  const roster = buildSyntheticCandidates();
  const winnerId = candidateIdOf(input.winnerParty, 1);
  const secondId = candidateIdOf(input.secondParty, 1);
  const sPct = secondPctOf(input.winnerPct);
  const pcts = new Map<string, number>([[winnerId, input.winnerPct], [secondId, sPct]]);
  const tail = Math.max(0, 100 - input.winnerPct - sPct);
  assignTail(roster.filter((c) => c.id !== winnerId && c.id !== secondId), tail, pcts);
  return pcts;
}

/**
 * Distribuicao de votos por candidato numa regiao: TODOS os candidatos com
 * votos/%/colocacao, ordenados por votos (1º = vencedor). Soma ≈ totalValidos.
 */
export function regionVoteDistribution(input: RegionVoteInput): readonly RegionVoteEntry[] {
  const pcts = distributionPercents(input);
  const scored = buildSyntheticCandidates().map((cand) => {
    // Todo candidato do roster recebe pct (vencedor/2º/cauda cobrem 100% do roster);
    // o `?? 0` so existe p/ o tipo `| undefined` do Map.get — inalcancavel em runtime.
    /* v8 ignore next */
    const pct = pcts.get(cand.id) ?? 0;
    return { cand, pct, votos: Math.round(pct / 100 * input.totalValidos) };
  });
  scored.sort((a, b) => b.votos - a.votos);
  return scored.map((entry, index) => ({ ...entry, posicao: index + 1 }));
}

/** Acha a entrada do candidato selecionado (por partido+ordem) ou a do 1º (agregado). */
function pickEntry(dist: readonly RegionVoteEntry[], sel: VoteCandidateSel | null): RegionVoteEntry | undefined {
  if (sel) return dist.find((e) => e.cand.party === sel.party && e.cand.ordem === sel.ordem);
  return dist.find((e) => e.posicao === 1);
}

/** VoteRow a partir de uma entrada do ranking (a fatia do candidato). */
function rowFromEntry(entry: RegionVoteEntry): VoteRow {
  return { votosAbsolutos: entry.votos, percentual: entry.pct, ranking: entry.posicao, party: entry.cand.party };
}

/** Grava a linha quando o candidato pontua (entrada existe e votos > 0). Compartilhado mun/zona. */
function addRow<K>(rows: Map<K, VoteRow>, key: K, entry: RegionVoteEntry | undefined): void {
  if (entry && entry.votos > 0) rows.set(key, rowFromEntry(entry));
}

// ── nivel municipio ──────────────────────────────────────────────────────────

/** Distribuicao de um municipio no ano (null sem resultado). */
export function munDistribution(munId: MunicipalityId, year: ElectoralYear): readonly RegionVoteEntry[] | null {
  const result = EXPLORE_DATASET.munis[munId]?.el[year];
  if (!result) return null;
  return regionVoteDistribution({ winnerParty: result.p, winnerPct: result.pct, secondParty: result.s, totalValidos: votosValidosDe(munId) });
}

/**
 * Mapa id→VoteRow do candidato (ou vencedor quando sel=null) por municipio. Pula
 * cidades sem resultado ou onde o candidato nao pontua. Puro: deriva do dataset.
 */
export function deriveVoteRows(year: ElectoralYear, sel: VoteCandidateSel | null): ReadonlyMap<MunicipalityId, VoteRow> {
  const rows = new Map<MunicipalityId, VoteRow>();
  for (const munId of Object.keys(EXPLORE_DATASET.munis)) {
    const dist = munDistribution(munId, year);
    addRow(rows, munId, dist ? pickEntry(dist, sel) : undefined);
  }
  return rows;
}

// ── nivel zona (close-up de Manaus) ──────────────────────────────────────────

/** Extrai o % do rotulo de zona ("MDB (44%)" -> 44). */
export function parseZonePct(label: string): number {
  const match = /\((\d+)%\)/.exec(label);
  return match ? Number(match[1]) : 0;
}

/** Extrai a sigla do rotulo de zona ("PT (33%)" -> "PT"; sem " (" -> o proprio). */
export function parseZoneParty(label: string): string {
  const at = label.indexOf(' (');
  return at >= 0 ? label.slice(0, at) : label;
}

/** Converte eleitores pt-BR ("412.000" -> 412000). */
export function parseEleitores(value: string | undefined): number {
  return value ? Number(value.replace(/\./g, '')) || 0 : 0;
}

/** Insumos da distribuicao a partir da tupla de zona (vencedor/2º parseados do rotulo). */
function zoneInputOf(zoneKey: ZonaKey, tuple: ZoneElectoralResult): RegionVoteInput {
  const totalValidos = parseEleitores(MANAUS_ZONA_POP_DATA[zoneKey]?.eleitores) * ZONE_TURNOUT;
  return { winnerParty: tuple[0], winnerPct: parseZonePct(tuple[2]), secondParty: parseZoneParty(tuple[3]), totalValidos };
}

/** Distribuicao de uma zona de Manaus no ano (insumos parseados do rotulo). */
export function zoneDistribution(zoneKey: ZonaKey, year: ElectoralYear): readonly RegionVoteEntry[] | null {
  const tuple = MANAUS_ELECTORAL_BY_YEAR[year]?.[zoneKey];
  if (!tuple) return null;
  return regionVoteDistribution(zoneInputOf(zoneKey, tuple));
}

/** Mapa zona→VoteRow do candidato (ou vencedor) nas 6 zonas de Manaus. */
export function deriveZoneVoteRows(year: ElectoralYear, sel: VoteCandidateSel | null): ReadonlyMap<ZonaKey, VoteRow> {
  const rows = new Map<ZonaKey, VoteRow>();
  const zoneResults = MANAUS_ELECTORAL_BY_YEAR[year];
  if (!zoneResults) return rows;
  for (const zoneKey of Object.keys(zoneResults) as ZonaKey[]) {
    const dist = regionVoteDistribution(zoneInputOf(zoneKey, zoneResults[zoneKey] as ZoneElectoralResult));
    addRow(rows, zoneKey, pickEntry(dist, sel));
  }
  return rows;
}

// ── escala de raio / calor (inalterado) ──────────────────────────────────────

/** Maior contagem absoluta entre as linhas (denominador da escala de raio). */
export function voteScaleMax(rows: Iterable<VoteRow>): number {
  let max = 0;
  for (const row of rows) if (row.votosAbsolutos > max) max = row.votosAbsolutos;
  return max || 1;
}

/** Raio (px) da bolha: base no min, max no topo; sqrt para AREA ∝ votos. */
export function raioDaBolha(votos: number, max: number): number {
  const span = BUBBLE_MAX_RADIUS - BUBBLE_BASE_RADIUS;
  return BUBBLE_BASE_RADIUS + span * Math.sqrt(clamp01(votos / (max || 1)));
}

/** Peso de calor 0..1 (normaliza a contagem pelo maximo). */
export function pesoDeCalor(votos: number, max: number): number {
  return clamp01(votos / (max || 1));
}

/**
 * Peso PERCEPTUAL de calor: sqrt do peso linear. Manaus domina tanto (≈100k× as
 * menores) que o linear apaga o resto; o sqrt da presenca as cidades medias sem
 * tirar Manaus do topo — mesma logica do raio da bolha (area ∝ votos).
 */
export function heatWeightOf(votos: number, max: number): number {
  return Math.sqrt(pesoDeCalor(votos, max));
}

/** Raio (px) do circulo de calor por peso (interpola min..max). */
export function heatRadiusFor(weight: number): number {
  return HEAT_MIN_RADIUS + (HEAT_MAX_RADIUS - HEAT_MIN_RADIUS) * clamp01(weight);
}

/** Opacidade do circulo de calor por peso (transparente onde baixo). */
export function heatOpacityFor(weight: number): number {
  return HEAT_MIN_OPACITY + (HEAT_MAX_OPACITY - HEAT_MIN_OPACITY) * clamp01(weight);
}

// ── matematica de cor (inalterado) ───────────────────────────────────────────

/** Le um canal hex (2 chars) de um '#rrggbb' na posicao dada (0/2/4). */
function channelAt(hex: string, at: number): number {
  return parseInt(hex.slice(1 + at, 3 + at), 16) || 0;
}

/** Interpola duas cores hex '#rrggbb' por t∈[0,1] (cor → cor), retornando '#RRGGBB'. */
export function mixHex(from: HexColor, to: HexColor, t: number): HexColor {
  const k = clamp01(t);
  const lerp = (at: number): string => {
    const value = Math.round(channelAt(from, at) + (channelAt(to, at) - channelAt(from, at)) * k);
    return value.toString(16).padStart(2, '0');
  };
  return `#${lerp(0)}${lerp(2)}${lerp(4)}`.toUpperCase();
}

/** Cor do circulo de calor: matiz do partido, esquentando ate o ink na ponta alta. */
export function heatColorFor(partyColor: HexColor, weight: number): HexColor {
  return mixHex(partyColor, INK, clamp01(weight) * 0.45);
}

/** Rampa de 3 swatches (baixo→médio→alto) na matiz do partido, p/ a legenda do calor. */
export function heatStopsForParty(partyColor: HexColor): readonly HexColor[] {
  return [mixHex(CLAY_WASH, partyColor, 0.55), partyColor, heatColorFor(partyColor, 1)];
}
